// Establishment Service - API integration for CRM Establishment Module
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://token-proxy-asd3etazf5a3h7fr.canadacentral-01.azurewebsites.net';

// Flag to use mock data when API is not ready
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === 'true' || false;

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('authToken') || '';
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 5000, // 5 second timeout
});

// Add request interceptor for authentication
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Helper function to check if error is a network error
const isNetworkError = (error) => {
  return error.code === 'ERR_NETWORK' || 
         error.message === 'Network Error' ||
         error.message?.includes('Network Error') ||
         !error.response;
};

// Mock data for development
const mockEstablishments = [
  {
    id: '1',
    postNumber: 'POST-001',
    postTitle: 'Senior Manager - Operations',
    siteCode: 'SITE-001',
    siteName: 'Head Office',
    fte: '1.0',
    jobStatus: 'Open',
    company: 'Sample Company',
  },
  {
    id: '2',
    postNumber: 'POST-002',
    postTitle: 'Support Worker',
    siteCode: 'SITE-002',
    siteName: 'Greenwich Pathway 1',
    fte: '0.5',
    jobStatus: 'Open',
    company: 'Sample Company',
  },
];

// Establishment CRUD Operations
export const establishmentService = {
  // Get all establishments with optional filters
  getEstablishments: async (filters = {}) => {
    try {
      const response = await apiClient.get('/api/establishment', { params: filters });
      return response.data;
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn('API not available, using mock data. Backend API will be available soon.');
        // Return mock data when API is not ready
        return mockEstablishments;
      }
      console.error('Error fetching establishments:', error);
      // Return empty array instead of throwing to prevent UI breakage
      return [];
    }
  },

  // Get single establishment by ID
  getEstablishmentById: async (id) => {
    try {
      const response = await apiClient.get(`/api/establishment/${id}`);
      return response.data;
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn('API not available, using mock data. Backend API will be available soon.');
        // Return mock establishment
        return mockEstablishments.find(e => e.id === id) || mockEstablishments[0];
      }
      console.error('Error fetching establishment:', error);
      return null;
    }
  },

  // Create new establishment
  createEstablishment: async (data) => {
    try {
      const response = await apiClient.post('/api/establishment', data);
      return response.data;
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn('API not available. Data will be saved when backend is ready.');
        // Return mock success response
        return { ...data, id: Date.now().toString(), message: 'Saved locally (API not available)' };
      }
      console.error('Error creating establishment:', error);
      throw error;
    }
  },

  // Update establishment
  updateEstablishment: async (id, data) => {
    try {
      const response = await apiClient.put(`/api/establishment/${id}`, data);
      return response.data;
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn('API not available. Data will be saved when backend is ready.');
        // Return mock success response
        return { ...data, id, message: 'Saved locally (API not available)' };
      }
      console.error('Error updating establishment:', error);
      throw error;
    }
  },

  // Delete establishment
  deleteEstablishment: async (id) => {
    try {
      const response = await apiClient.delete(`/api/establishment/${id}`);
      return response.data;
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn('API not available. Delete will be processed when backend is ready.');
        return { message: 'Deleted locally (API not available)' };
      }
      console.error('Error deleting establishment:', error);
      throw error;
    }
  },

  // Get establishment history
  getEstablishmentHistory: async (id) => {
    try {
      const response = await apiClient.get(`/api/establishment/${id}/history`);
      return response.data;
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn('API not available, using mock history. Backend API will be available soon.');
        // Return empty history array
        return [];
      }
      console.error('Error fetching establishment history:', error);
      return [];
    }
  },
};

// Establishment Detail (Worker Assignments) Operations
export const establishmentDetailService = {
  // Get worker assignments for an establishment
  getWorkerAssignments: async (establishmentId) => {
    try {
      const response = await apiClient.get(`/api/establishment/${establishmentId}/workers`);
      return response.data;
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn('API not available, returning empty worker assignments.');
        return [];
      }
      console.error('Error fetching worker assignments:', error);
      return [];
    }
  },

  // Create worker assignment
  createWorkerAssignment: async (data) => {
    try {
      const response = await apiClient.post('/api/establishment-detail', data);
      return response.data;
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn('API not available. Data will be saved when backend is ready.');
        return { ...data, id: Date.now().toString(), message: 'Saved locally (API not available)' };
      }
      console.error('Error creating worker assignment:', error);
      throw error;
    }
  },

  // Update worker assignment
  updateWorkerAssignment: async (id, data) => {
    try {
      const response = await apiClient.put(`/api/establishment-detail/${id}`, data);
      return response.data;
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn('API not available. Data will be saved when backend is ready.');
        return { ...data, id, message: 'Saved locally (API not available)' };
      }
      console.error('Error updating worker assignment:', error);
      throw error;
    }
  },
};

// Establishment Split Code Operations
export const establishmentSplitCodeService = {
  // Get split codes for an establishment
  getSplitCodes: async (establishmentId) => {
    try {
      const response = await apiClient.get(`/api/establishment/${establishmentId}/split-codes`);
      return response.data;
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn('API not available, returning empty split codes.');
        return [];
      }
      console.error('Error fetching split codes:', error);
      return [];
    }
  },

  // Create split code
  createSplitCode: async (data) => {
    try {
      const response = await apiClient.post('/api/establishment-split-code', data);
      return response.data;
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn('API not available. Data will be saved when backend is ready.');
        return { ...data, id: Date.now().toString(), message: 'Saved locally (API not available)' };
      }
      console.error('Error creating split code:', error);
      throw error;
    }
  },

  // Update split code
  updateSplitCode: async (id, data) => {
    try {
      const response = await apiClient.put(`/api/establishment-split-code/${id}`, data);
      return response.data;
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn('API not available. Data will be saved when backend is ready.');
        return { ...data, id, message: 'Saved locally (API not available)' };
      }
      console.error('Error updating split code:', error);
      throw error;
    }
  },

  // Delete split code
  deleteSplitCode: async (id) => {
    try {
      const response = await apiClient.delete(`/api/establishment-split-code/${id}`);
      return response.data;
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn('API not available. Delete will be processed when backend is ready.');
        return { message: 'Deleted locally (API not available)' };
      }
      console.error('Error deleting split code:', error);
      throw error;
    }
  },

  // Validate split percentages
  validateSplitPercentages: (splitCodes) => {
    const total = splitCodes.reduce((sum, code) => sum + (parseFloat(code.splitPercentage) || 0), 0);
    return Math.abs(total - 100) < 0.01; // Allow for floating point errors
  },
};

export default {
  establishment: establishmentService,
  detail: establishmentDetailService,
  splitCode: establishmentSplitCodeService,
};

